import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getStrategies, createBacktest, runBacktest, validateSymbol } from '../api';
import SymbolSuggestion from '../components/SymbolSuggestion';
import TickerAutocomplete from '../components/TickerAutocomplete';
import { track, Events } from '../analytics';
import { useBackgroundRuns } from '../context/BackgroundRunsContext';

// Mirrors strategies/data_service.py's _YFINANCE_INTRADAY_LOOKBACK_DAYS.
const STOCK_INTRADAY_LOOKBACK_DAYS = { '1m': 7, '5m': 60, '15m': 60, '30m': 60, '1h': 730 };

const ASSET_LABELS = { stock: 'Stock', fx: 'Forex', crypto: 'Crypto' };

// Persist the form inputs for the browser-tab session so navigating to results and
// clicking Back doesn't reset everything to defaults.
const FORM_STORAGE_KEY = 'backtestForm';
function readSavedForm() {
  try { return JSON.parse(sessionStorage.getItem(FORM_STORAGE_KEY)) || {}; } catch { return {}; }
}

// Fresh default date range: 1-day bars over the trailing year ending *yesterday*
// (yesterday, not today, so the end lands on a settled trading session). Computed
// at load so a strategy tested months from now still gets a sensible recent window
// instead of a hardcoded stale range.
function defaultDates() {
  const end = new Date(Date.now() - 86400000);          // yesterday
  const start = new Date(end);
  start.setFullYear(start.getFullYear() - 1);           // one year earlier
  return { startDate: start.toISOString().slice(0, 10), endDate: end.toISOString().slice(0, 10) };
}

export default function BacktestRunner({ onResult, preselectedStrategyId, onRequiresSignup }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const saved = useMemo(readSavedForm, []);
  // Deep-link support (e.g. new-user activation emails): /backtest?strategy=<id>&autorun=1
  // opens a specific strategy and, once it's loaded, runs the backtest automatically.
  const deepLinkStrategyId = useMemo(() => searchParams.get('strategy') || '', []); // eslint-disable-line react-hooks/exhaustive-deps
  const deepLinkAutorun = useMemo(() => searchParams.get('autorun') === '1', []); // eslint-disable-line react-hooks/exhaustive-deps
  const defaults = useMemo(defaultDates, []);
  // A strategy arriving via preselect/deep-link that differs from the one in the
  // saved form means the user is starting a *new* test — so ignore the saved
  // inputs and fall back to fresh defaults. Returning from Results (same strategy,
  // or no incoming strategy) keeps the saved form so Back doesn't wipe your inputs.
  const incomingStrategy = deepLinkStrategyId || preselectedStrategyId || '';
  const isFreshStrategyVisit = !!incomingStrategy && String(incomingStrategy) !== String(saved.selectedStrategy || '');
  const init = isFreshStrategyVisit ? {} : saved;
  const [strategies, setStrategies] = useState([]);
  const [selectedStrategy, setSelectedStrategy] = useState(incomingStrategy || saved.selectedStrategy || '');
  const [assetType, setAssetType] = useState(init.assetType || 'stock');
  const [symbol, setSymbol] = useState(init.symbol || 'AAPL');
  const [startDate, setStartDate] = useState(init.startDate || defaults.startDate);
  const [endDate, setEndDate] = useState(init.endDate || defaults.endDate);
  const [initialCash, setInitialCash] = useState(init.initialCash ?? 100000);
  const [slippage, setSlippage] = useState(init.slippage ?? 0.1);
  const [commission, setCommission] = useState(init.commission ?? 0.1);
  const [fillOnClose, setFillOnClose] = useState(init.fillOnClose ?? false);
  const [barInterval, setBarInterval] = useState(init.barInterval || '1d');
  const [showAdvanced, setShowAdvanced] = useState(init.showAdvanced ?? false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  // Per-field validation messages, keyed by the same names as `fieldErrors`.
  // Rendered inline under each field so the problem reads as clearly as the ticker.
  const [fieldMessages, setFieldMessages] = useState({});
  // Maps field name -> input DOM node so a failed validation can scroll the first
  // offending field into view and focus it (the form is long once Advanced is open).
  const fieldRefs = useRef({});
  const { startRun } = useBackgroundRuns();
  const [queued, setQueued] = useState(false);
  const [corrTickers, setCorrTickers] = useState(init.corrTickers ?? '');
  const [corrExternal, setCorrExternal] = useState(init.corrExternal ?? []);
  const isFirstRender = useRef(true);
  // Async symbol validation: { status: 'idle'|'checking'|'valid'|'invalid'|'ambiguous', msg }
  const [symbolCheck, setSymbolCheck] = useState({ status: 'idle', msg: '' });
  const symbolCheckSeq = useRef(0);
  // When the user resolves an ambiguous ticker (or applies a suggestion) we pin
  // the chosen class so re-validation confirms *that* class instead of re-
  // detecting the collision. Cleared whenever the symbol text is edited.
  const [lockedClass, setLockedClass] = useState(null);

  // The selected strategy's declared symbols (defined in the builder). Index 0
  // is the primary. Empty => ordinary single-symbol strategy.
  const strategyUniverse = useMemo(() => {
    const s = strategies.find((x) => String(x.id) === String(selectedStrategy));
    return s?.graph?.universe || [];
  }, [strategies, selectedStrategy]);
  const isMultiAsset = strategyUniverse.length > 0;

  // Editable per-slot tickers for a multi-asset strategy, pre-filled from the
  // strategy but overridable so a saved strategy can be re-run on other tickers.
  const [universeSymbols, setUniverseSymbols] = useState([]);
  useEffect(() => {
    if (strategyUniverse.length > 0) {
      setUniverseSymbols(strategyUniverse.map((u) => u.symbol));
      // The primary's asset type drives interval options / price formatting.
      setAssetType(strategyUniverse[0].asset_type || 'stock');
    }
  }, [strategyUniverse]);
  const updateUniverseSymbol = (i, val) => {
    setUniverseSymbols((prev) => prev.map((s, idx) => (idx === i ? val : s)));
  };

  // Per-slot ticker validation for multi-asset strategies (debounced), mirroring
  // the single-symbol check below. Advisory + soft-blocks the run on an invalid.
  const [slotChecks, setSlotChecks] = useState([]); // [{ status, msg }]
  const slotSeq = useRef(0);
  useEffect(() => {
    if (!isMultiAsset) { setSlotChecks([]); return undefined; }
    const seq = ++slotSeq.current;
    setSlotChecks(universeSymbols.map((s) =>
      (s || '').trim() ? { status: 'checking', msg: '' } : { status: 'idle', msg: '' }
    ));
    const t = setTimeout(async () => {
      const results = await Promise.all(universeSymbols.map(async (s, i) => {
        const sym = (s || '').trim().toUpperCase();
        if (!sym) return { status: 'idle', msg: '' };
        const at = strategyUniverse[i]?.asset_type || 'stock';
        try {
          const { data } = await validateSymbol(sym, at);
          return data?.valid
            ? { status: 'valid', msg: '', data }
            : { status: 'invalid', msg: data?.error || `${sym} doesn't look valid.`, data };
        } catch {
          return { status: 'idle', msg: '' }; // network/permission hiccup — stay silent
        }
      }));
      if (seq !== slotSeq.current) return;
      setSlotChecks(results);
    }, 500);
    return () => clearTimeout(t);
  }, [universeSymbols, isMultiAsset, strategyUniverse]);

  // Persist inputs on every change so they survive unmount (navigating to results).
  useEffect(() => {
    try {
      sessionStorage.setItem(FORM_STORAGE_KEY, JSON.stringify({
        selectedStrategy, assetType, symbol, startDate, endDate, initialCash,
        slippage, commission, fillOnClose, barInterval, showAdvanced, corrTickers, corrExternal,
      }));
    } catch { /* ignore storage quota/serialization errors */ }
  }, [selectedStrategy, assetType, symbol, startDate, endDate, initialCash,
      slippage, commission, fillOnClose, barInterval, showAdvanced, corrTickers, corrExternal]);

  useEffect(() => {
    getStrategies().then((res) => setStrategies(res.data)).catch(() => {});
  }, []);

  // Validate the symbol against real market data as the user types (debounced).
  // We auto-detect the asset class (no manual selector) restricted to stock/fx
  // (crypto backtests aren't live). The resolver drives `assetType` from what
  // actually has data, and flags a collision (e.g. a ticker that's both a stock
  // and an FX symbol) as 'ambiguous' so we can ask which market.
  useEffect(() => {
    const sym = (symbol || '').trim().toUpperCase();
    if (!sym) { setSymbolCheck({ status: 'idle', msg: '' }); return undefined; }
    // Cheap format gate so we don't probe obviously-incomplete input. Permissive
    // on purpose: the backend probe is the real authority, and symbols we surface
    // include indices (^GSPC) and futures (NQ=F), so '^' and '=' pass through.
    if (!/^[A-Z0-9.^=-]{1,20}$/.test(sym)) {
      setSymbolCheck({ status: 'idle', msg: '' });
      return undefined;
    }
    const seq = ++symbolCheckSeq.current;
    setSymbolCheck({ status: 'checking', msg: '' });
    const t = setTimeout(() => {
      // Pinned class (a resolved collision / applied suggestion) confirms that
      // one class; otherwise auto-detect across stock/fx.
      validateSymbol(sym, lockedClass || 'auto', lockedClass ? undefined : 'stock,fx')
        .then(({ data }) => {
          if (seq !== symbolCheckSeq.current) return; // superseded by newer input
          if (data?.valid) {
            if (data.asset_type) setAssetType(data.asset_type); // adopt the detected class
            setSymbolCheck({ status: 'valid', msg: '', data });
          } else if (data?.ambiguous) {
            setSymbolCheck({ status: 'ambiguous', msg: data?.error || '', data });
          } else {
            setSymbolCheck({ status: 'invalid', msg: data?.error || `${sym} doesn't look valid.`, data });
          }
        })
        .catch(() => {
          if (seq !== symbolCheckSeq.current) return;
          setSymbolCheck({ status: 'idle', msg: '' }); // network hiccup — stay silent
        });
    }, 500);
    return () => clearTimeout(t);
  }, [symbol, lockedClass]);

  useEffect(() => {
    if (preselectedStrategyId) setSelectedStrategy(preselectedStrategyId);
  }, [preselectedStrategyId]);

  // Reset interval + slippage/commission when the user *changes* asset type. Skipped
  // on the initial mount so a form restored from sessionStorage keeps its values.
  // (Forex is typically commission-free with tight broker-absorbed spreads, so it
  // defaults slippage/commission to 0 vs. the stock/crypto defaults.)
  useEffect(() => {
    if (isFirstRender.current) return;
    setBarInterval('1d');
    if (assetType === 'fx') {
      setSlippage(0);
      setCommission(0);
    } else {
      setSlippage(0.1);
      setCommission(0.1);
    }
  }, [assetType]);

  // Mark the initial mount complete (runs after the effects above on first render).
  useEffect(() => { isFirstRender.current = false; }, []);

  // Stocks/crypto only have a limited intraday history window (yfinance). Auto-clamp
  // the start date so switching to an intraday interval doesn't immediately fail.
  useEffect(() => {
    if (assetType === 'fx') return;
    const lookbackDays = STOCK_INTRADAY_LOOKBACK_DAYS[barInterval];
    if (!lookbackDays) return;
    const earliestAllowed = new Date();
    earliestAllowed.setDate(earliestAllowed.getDate() - lookbackDays);
    if (new Date(startDate) < earliestAllowed) {
      setStartDate(earliestAllowed.toISOString().slice(0, 10));
      setEndDate(new Date().toISOString().slice(0, 10));
    }
  }, [barInterval, assetType]); // eslint-disable-line react-hooks/exhaustive-deps


  // A daily-priced instrument (mutual fund NAV) can't back intraday bars — the
  // run would fail on "not enough data" however the dates are set. Warn up front.
  const isStockIntraday = assetType !== 'fx' && !!STOCK_INTRADAY_LOOKBACK_DAYS[barInterval];
  const dailyOnlySymbol = !isMultiAsset && symbolCheck.status === 'valid'
    && symbolCheck.data?.quote_type === 'Fund' && isStockIntraday;

  const intervalOptionsByAsset = {
    fx: {
      groups: [
        { label: 'Daily / Weekly', options: [['1d', '1 Day'], ['1w', '1 Week'], ['1mo', '1 Month']] },
        { label: 'Intraday', options: [['4h', '4 Hours'], ['1h', '1 Hour'], ['30m', '30 Minutes'], ['15m', '15 Minutes'], ['10m', '10 Minutes'], ['5m', '5 Minutes'], ['1m', '1 Minute']] },
        { label: 'Tick / Seconds', options: [['30s', '30 Seconds'], ['10s', '10 Seconds'], ['1s', '1 Second']] },
      ],
      note: 'Sub-minute intervals may be slow to load.',
    },
    stock: {
      groups: [
        { label: 'Daily / Weekly', options: [['1d', '1 Day'], ['1w', '1 Week'], ['1mo', '1 Month']] },
        { label: 'Intraday', options: [['1h', '1 Hour · past 2 yrs'], ['30m', '30 Minutes · past 60 days'], ['15m', '15 Minutes · past 60 days'], ['5m', '5 Minutes · past 60 days'], ['1m', '1 Minute · past 7 days']] },
      ],
      note: 'Intraday history is limited to a recent window per interval (shown above).',
    },
  };
  intervalOptionsByAsset.crypto = intervalOptionsByAsset.stock;

  const clearFieldError = (name) => {
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
    setFieldMessages((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  // Fields in top-to-bottom DOM order, used to scroll to the first invalid one.
  const ORDERED_FIELDS = ['strategy', 'symbol', 'start_date', 'end_date', 'initial_cash', 'slippage_perc', 'commission_perc'];
  const ADVANCED_FIELDS = new Set(['slippage_perc', 'commission_perc']);

  // After a failed validation, reveal and focus the first offending field so the
  // user isn't left staring at an error message while the red field is scrolled
  // off-screen (common when Advanced Settings is expanded).
  const focusFirstError = (errors) => {
    const firstBad = ORDERED_FIELDS.find((f) => errors[f]);
    if (!firstBad) return;
    if (ADVANCED_FIELDS.has(firstBad)) setShowAdvanced(true);
    // Defer a tick so a just-expanded Advanced panel is mounted before we scroll.
    setTimeout(() => {
      const el = fieldRefs.current[firstBad];
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus({ preventScroll: true });
    }, 60);
  };

  const handleRun = async () => {
    const errors = {};
    const fieldMsgs = {};
    const messages = [];
    // Record an error for `field` with an inline `msg`; keeps the first message
    // per field (for the inline text) while preserving overall order (for the banner).
    const addErr = (field, msg) => {
      errors[field] = true;
      if (!fieldMsgs[field]) fieldMsgs[field] = msg;
      messages.push(msg);
    };
    const sym = symbol.trim().toUpperCase();
    const today = new Date().toISOString().slice(0, 10);

    if (!selectedStrategy) { addErr('strategy', 'Please select a strategy.'); }

    if (isMultiAsset) {
      // Every slot must have a ticker, and any slot we've confirmed invalid blocks the run.
      const blanks = universeSymbols.filter((s) => !(s || '').trim());
      if (blanks.length) { addErr('symbol', 'Please fill in every symbol field.'); }
      const badIdx = slotChecks.findIndex((c) => c?.status === 'invalid');
      if (badIdx !== -1) {
        addErr('symbol', slotChecks[badIdx].msg || `${(universeSymbols[badIdx] || '').toUpperCase()} isn't a valid symbol.`);
      }
    } else if (!sym) {
      addErr('symbol', 'Please enter a symbol.');
    } else if (symbolCheck.status === 'invalid') {
      addErr('symbol', symbolCheck.msg || 'Enter a valid ticker symbol.');
    } else if (symbolCheck.status === 'ambiguous') {
      addErr('symbol', `${sym} trades in more than one market — pick which one below.`);
    } else if (dailyOnlySymbol) {
      addErr('symbol', `${sym} only prices once a day, so ${barInterval} bars aren't available. Switch to 1 Day or pick another ticker.`);
    } else if (!/^[A-Z0-9.^=-]{1,20}$/.test(sym)) {
      // Last-resort format gate for input submitted before the async check ran.
      // Permissive: allows indices (^GSPC) and futures (NQ=F); the backend probe
      // is the real authority and rejects anything with no data.
      addErr('symbol', 'Enter a valid ticker symbol.');
    }

    const cashNum = Number(initialCash);
    if (!Number.isFinite(cashNum) || cashNum <= 0) { addErr('initial_cash', 'Initial cash must be greater than 0.'); }

    const slipNum = Number(slippage);
    if (!Number.isFinite(slipNum) || slipNum < 0 || slipNum > 100) { addErr('slippage_perc', 'Slippage must be between 0 and 100%.'); }
    const commNum = Number(commission);
    if (!Number.isFinite(commNum) || commNum < 0 || commNum > 100) { addErr('commission_perc', 'Commission must be between 0 and 100%.'); }

    if (!startDate) { addErr('start_date', 'Please choose a start date.'); }
    if (!endDate) { addErr('end_date', 'Please choose an end date.'); }
    if (startDate && endDate && startDate >= endDate) {
      addErr('start_date', 'Start date must be before end date.');
      errors.end_date = true;
      if (!fieldMsgs.end_date) fieldMsgs.end_date = 'Start date must be before end date.';
    }
    if (endDate && endDate > today) { addErr('end_date', 'End date cannot be in the future.'); }

    // Intraday lookback: yfinance only serves a limited window for stocks/crypto.
    // The auto-clamp on interval change covers the common case, but a manually
    // edited start date can still slip through — historically the single biggest
    // source of backtest failures — so block it explicitly with a clear message
    // that mirrors the backend's, before we ever hit the server.
    if (assetType !== 'fx' && startDate) {
      const lookbackDays = STOCK_INTRADAY_LOOKBACK_DAYS[barInterval];
      if (lookbackDays) {
        const earliest = new Date();
        earliest.setDate(earliest.getDate() - lookbackDays);
        const earliestStr = earliest.toISOString().slice(0, 10);
        if (startDate < earliestStr) {
          addErr(
            'start_date',
            `${barInterval} bars are only available for the past ${lookbackDays} days. `
            + `Choose a start date on or after ${earliestStr}, or use a larger bar interval.`
          );
        }
      }
    }

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      setFieldMessages(fieldMsgs);
      setError(messages[0] || 'Please check the highlighted fields.');
      focusFirstError(errors);
      return;
    }
    setError('');
    setFieldErrors({});
    setFieldMessages({});
    setQueued(false);
    setRunning(true);
    // Build the run universe. Multi-asset strategies submit the editable per-slot
    // tickers (primary first); single-symbol strategies submit just the one symbol.
    const runSymbols = isMultiAsset
      ? universeSymbols.map((s, i) => ({
          symbol: (s || '').trim().toUpperCase(),
          asset_type: strategyUniverse[i]?.asset_type || 'stock',
        }))
      : [];
    const primarySym = isMultiAsset ? runSymbols[0].symbol : symbol.toUpperCase();
    const primaryAsset = isMultiAsset ? runSymbols[0].asset_type : assetType;
    track(Events.BACKTEST_STARTED, {
      strategy_id: selectedStrategy,
      asset_type: primaryAsset,
      symbol: primarySym,
    });
    try {
      // Create the backtest record. For multi-asset strategies `symbols` is the
      // full editable universe (primary first); the backend prepends the primary.
      const createResp = await createBacktest({
        strategy: selectedStrategy,
        asset_type: primaryAsset,
        symbol: primarySym,
        symbols: runSymbols,
        start_date: startDate,
        end_date: endDate,
        initial_cash: initialCash,
        slippage_perc: slippage,
        commission_perc: commission,
        fill_on_close: fillOnClose,
        fx_interval: barInterval,
      });
      // Execute it
      const corrTickerList = corrTickers.split(',').map(t => t.trim().toUpperCase()).filter(Boolean);
      const runResp = await runBacktest(createResp.data.id, {
        correlation_tickers: corrTickerList,
        correlation_external: corrExternal,
      });
      if (runResp.status === 202) {
        // Large backtest queued — the progress modal polls and hands back the
        // finished result via onComplete when the user clicks "View results".
        setQueued(true);
        startRun({
          backtestId: createResp.data.id,
          kind: 'backtest',
          onComplete: (data) => {
            setQueued(false);
            if (onResult) onResult(data);
          },
        });
      } else {
        if (onResult) onResult(runResp.data);
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.requires_signup && onRequiresSignup) {
        onRequiresSignup(data.detail);
      } else if (data && typeof data === 'object' && !data.detail) {
        // DRF field-validation errors, e.g. {"symbol": ["This field may not be blank."]}
        const knownFields = ['strategy', 'symbol', 'start_date', 'end_date', 'initial_cash', 'slippage_perc', 'commission_perc', 'fx_interval', 'asset_type'];
        const errs = {};
        const msgs = {};
        const messages = [];
        Object.entries(data).forEach(([field, fieldMsgs]) => {
          const text = Array.isArray(fieldMsgs) ? fieldMsgs.join(' ') : String(fieldMsgs);
          if (knownFields.includes(field)) { errs[field] = true; msgs[field] = text; }
          messages.push(text);
        });
        setFieldErrors(errs);
        setFieldMessages(msgs);
        setError(messages.join(' ') || 'Please check the highlighted fields.');
        focusFirstError(errs);
      } else {
        setError(data?.detail || err.message);
      }
    } finally {
      setRunning(false);
    }
  };

  // Auto-run a deep-linked backtest (/backtest?strategy=<id>&autorun=1) once the
  // strategy list has loaded and the linked strategy actually exists. Fires at
  // most once, and strips the query params so a remount (e.g. Back from results)
  // doesn't re-trigger it.
  const autoRunFired = useRef(false);
  useEffect(() => {
    if (autoRunFired.current || !deepLinkAutorun || running) return;
    if (!strategies.length || !selectedStrategy) return;
    const found = strategies.find((x) => String(x.id) === String(selectedStrategy));
    if (!found) return;
    autoRunFired.current = true;
    setSearchParams({}, { replace: true });
    handleRun();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategies, selectedStrategy, deepLinkAutorun, running]);

  return (
    <div className="backtest-runner" style={{
      maxWidth: 500,
      margin: '0 auto',
      padding: 24,
    }}>
      <Helmet>
        <title>Run Backtest — Algovex</title>
        <meta name="description" content="Backtest your algorithmic trading strategy on historical data for stocks, forex, and crypto on Algovex." />
        <meta name="robots" content="noindex" />
      </Helmet>

      <label style={labelStyle}>Strategy</label>
      <select
        ref={(el) => { fieldRefs.current.strategy = el; }}
        value={selectedStrategy}
        onChange={(e) => {
          const next = e.target.value;
          // Switching to a *different* strategy = a new test: reset the run window
          // (dates + interval) to fresh defaults rather than carrying the last run's.
          if (next && String(next) !== String(selectedStrategy)) {
            setStartDate(defaults.startDate);
            setEndDate(defaults.endDate);
            setBarInterval('1d');
          }
          setSelectedStrategy(next);
          if (next) clearFieldError('strategy');
        }}
        style={fieldErrors.strategy ? { ...inputStyle, ...invalidInputStyle } : inputStyle}
      >
        <option value="">-- Select a strategy --</option>
        {strategies.map((s) => (
          <option key={s.id} value={s.id}>{s.name}</option>
        ))}
      </select>
      {fieldMessages.strategy && <div style={fieldMsgStyle}>{fieldMessages.strategy}</div>}

      {isMultiAsset ? (
        <>
          <label style={labelStyle}>Symbols</label>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>
            This is a multi-asset strategy. Each field is editable — override a ticker to run
            it on a different instrument. The ● primary drives the timeline &amp; benchmark.
          </div>
          {universeSymbols.map((sym, i) => {
            const check = slotChecks[i] || { status: 'idle', msg: '' };
            const invalid = check.status === 'invalid';
            return (
              <div key={i} style={{ marginBottom: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 78, fontSize: 12, color: 'var(--muted)', flexShrink: 0 }}>
                    {i === 0 ? '● Primary' : `Symbol ${i + 1}`}
                  </span>
                  <TickerAutocomplete
                    inputRef={i === 0 ? (el) => { fieldRefs.current.symbol = el; } : undefined}
                    value={sym}
                    onChange={(val) => updateUniverseSymbol(i, val)}
                    onPick={(s) => updateUniverseSymbol(i, s)}
                    classes={strategyUniverse[i]?.asset_type === 'fx' ? 'fx' : 'stock,fx'}
                    placeholder={strategyUniverse[i]?.symbol}
                    wrapperStyle={{ flex: 1 }}
                    style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', textTransform: 'uppercase', margin: 0 }}
                    invalidStyle={invalidInputStyle}
                    invalid={!!fieldErrors.symbol || invalid}
                  />
                  <span style={{ fontSize: 10, color: 'var(--muted)', width: 42, flexShrink: 0 }}>
                    {strategyUniverse[i]?.asset_type || 'stock'}
                  </span>
                </div>
                {check.status !== 'idle' && (
                  <div style={{
                    fontSize: 11, marginTop: 2, marginLeft: 86,
                    color: check.status === 'valid' ? 'var(--green, #26a641)'
                      : check.status === 'invalid' ? 'var(--red, #f85149)' : 'var(--muted)',
                  }}>
                    {check.status === 'checking' && 'Checking…'}
                    {check.status === 'valid' && '✓ Valid'}
                    {check.status === 'invalid' && check.msg}
                  </div>
                )}
                {check.status === 'invalid' && (
                  <div style={{ marginLeft: 86 }}>
                    <SymbolSuggestion result={check.data} compact onApply={(s) => updateUniverseSymbol(i, s)} />
                  </div>
                )}
              </div>
            );
          })}
          {fieldMessages.symbol && <div style={fieldMsgStyle}>{fieldMessages.symbol}</div>}
        </>
      ) : (
        <>
          <label style={labelStyle}>Symbol</label>
          <TickerAutocomplete
            inputRef={(el) => { fieldRefs.current.symbol = el; }}
            value={symbol}
            onChange={(val) => {
              setSymbol(val);
              setLockedClass(null); // typing means re-detect from scratch
              if (val.trim()) clearFieldError('symbol');
            }}
            onPick={(sym, at) => {
              // Picking a suggestion pins its class so re-validation confirms it
              // rather than re-detecting, mirroring the "did you mean" apply below.
              if (at) { setLockedClass(at); setAssetType(at); }
              setSymbol(sym);
              clearFieldError('symbol');
            }}
            classes="stock,fx"
            placeholder="e.g. AAPL, EURUSD, ^GSPC, NQ"
            style={inputStyle}
            invalidStyle={invalidInputStyle}
            invalid={!!fieldErrors.symbol}
          />
          {fieldMessages.symbol && symbolCheck.status !== 'invalid' && symbolCheck.status !== 'ambiguous' && (
            <div style={fieldMsgStyle}>{fieldMessages.symbol}</div>
          )}
          {symbolCheck.status === 'checking' && (
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Checking symbol…</div>
          )}
          {symbolCheck.status === 'valid' && (
            <div style={{ fontSize: 11, color: 'var(--green, #26a641)', marginTop: 4 }}>
              ✓ Valid{ASSET_LABELS[symbolCheck.data?.asset_type] ? ` · ${ASSET_LABELS[symbolCheck.data.asset_type]}` : ''}
              {symbolCheck.data?.name && (
                <span style={{ color: 'var(--muted)' }}>
                  {' · '}{symbolCheck.data.quote_type ? `${symbolCheck.data.quote_type}: ` : ''}{symbolCheck.data.name}
                </span>
              )}
            </div>
          )}
          {dailyOnlySymbol && (
            <div style={{ fontSize: 11, color: 'var(--orange, #db6d28)', marginTop: 4 }}>
              {symbol.trim().toUpperCase()} is a mutual fund — it only prices once a day, so {barInterval} bars
              aren't available.{' '}
              <button
                type="button"
                onClick={() => { setBarInterval('1d'); clearFieldError('symbol'); }}
                style={{ background: 'none', border: 'none', padding: 0, color: 'var(--accent, #58a6ff)', cursor: 'pointer', fontSize: 11, textDecoration: 'underline' }}
              >Use 1 Day bars</button>
              {' '}or check the ticker.
            </div>
          )}
          {(symbolCheck.status === 'invalid' || symbolCheck.status === 'ambiguous') && symbolCheck.msg && (
            <div style={{ fontSize: 11, color: symbolCheck.status === 'ambiguous' ? 'var(--muted)' : 'var(--red, #f85149)', marginTop: 4 }}>{symbolCheck.msg}</div>
          )}
          {(symbolCheck.status === 'invalid' || symbolCheck.status === 'ambiguous') && (
            <SymbolSuggestion
              result={symbolCheck.data}
              onApply={(s, at) => {
                // Pin the picked class so re-validation confirms it (rather than
                // re-detecting the same collision), then apply the symbol.
                if (at) { setLockedClass(at); setAssetType(at); }
                setSymbol(s);
                clearFieldError('symbol');
              }}
            />
          )}
        </>
      )}

      {intervalOptionsByAsset[assetType] && (
        <>
          <label style={labelStyle}>Bar Interval</label>
          <select value={barInterval} onChange={(e) => setBarInterval(e.target.value)} style={inputStyle}>
            {intervalOptionsByAsset[assetType].groups.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.options.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </optgroup>
            ))}
          </select>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
            {isStockIntraday ? (() => {
              const days = STOCK_INTRADAY_LOOKBACK_DAYS[barInterval];
              const earliest = new Date();
              earliest.setDate(earliest.getDate() - days);
              return `${barInterval} history covers the past ${days} days (start on or after ${earliest.toISOString().slice(0, 10)}). `
                + 'Indicator warmup is loaded automatically before your start date, so short ranges still work.';
            })() : intervalOptionsByAsset[assetType].note}
          </div>
        </>
      )}

      <div style={{ display: 'flex', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Start Date</label>
          <input
            ref={(el) => { fieldRefs.current.start_date = el; }}
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              clearFieldError('start_date');
            }}
            style={fieldErrors.start_date ? { ...inputStyle, ...invalidInputStyle } : inputStyle}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>End Date</label>
          <input
            ref={(el) => { fieldRefs.current.end_date = el; }}
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              clearFieldError('end_date');
            }}
            style={fieldErrors.end_date ? { ...inputStyle, ...invalidInputStyle } : inputStyle}
          />
        </div>
      </div>
      {(fieldMessages.start_date || fieldMessages.end_date) && (
        <div style={fieldMsgStyle}>{fieldMessages.start_date || fieldMessages.end_date}</div>
      )}

      <label style={labelStyle}>Initial Cash ($)</label>
      <input
        ref={(el) => { fieldRefs.current.initial_cash = el; }}
        type="number"
        value={initialCash}
        onChange={(e) => { setInitialCash(parseFloat(e.target.value) || 100000); clearFieldError('initial_cash'); }}
        style={fieldErrors.initial_cash ? { ...inputStyle, ...invalidInputStyle } : inputStyle}
        min={1000}
      />
      {fieldMessages.initial_cash && <div style={fieldMsgStyle}>{fieldMessages.initial_cash}</div>}

      {/* Advanced Settings Toggle */}
      <button
        onClick={() => setShowAdvanced((v) => !v)}
        style={{
          marginTop: 16,
          background: 'none',
          border: 'none',
          color: 'var(--blue)',
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: 600,
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <span style={{ transition: 'transform 0.2s', transform: showAdvanced ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block' }}>▶</span>
        Advanced Settings
      </button>

      {showAdvanced && (
        <div style={{
          marginTop: 8,
          padding: '12px 14px',
          background: 'var(--card)',
          border: '1px solid var(--border)',
          borderRadius: 8,
        }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Slippage (%)</label>
              <input
                ref={(el) => { fieldRefs.current.slippage_perc = el; }}
                type="number"
                value={slippage}
                onChange={(e) => { setSlippage(parseFloat(e.target.value) || 0); clearFieldError('slippage_perc'); }}
                style={fieldErrors.slippage_perc ? { ...inputStyle, ...invalidInputStyle } : inputStyle}
                min={0}
                max={10}
                step={0.01}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Commission (%)</label>
              <input
                ref={(el) => { fieldRefs.current.commission_perc = el; }}
                type="number"
                value={commission}
                onChange={(e) => { setCommission(parseFloat(e.target.value) || 0); clearFieldError('commission_perc'); }}
                style={fieldErrors.commission_perc ? { ...inputStyle, ...invalidInputStyle } : inputStyle}
                min={0}
                max={10}
                step={0.01}
              />
            </div>
          </div>
          {(fieldMessages.slippage_perc || fieldMessages.commission_perc) && (
            <div style={fieldMsgStyle}>{fieldMessages.slippage_perc || fieldMessages.commission_perc}</div>
          )}

          <label style={{ ...labelStyle, marginTop: 12 }}>Order Fill Timing</label>
          <select
            value={fillOnClose ? 'close' : 'open'}
            onChange={(e) => setFillOnClose(e.target.value === 'close')}
            style={inputStyle}
          >
            <option value="open">Next bar's open (realistic)</option>
            <option value="close">Current bar's close (cheat-on-close)</option>
          </select>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
            "Next bar's open" is more realistic — orders can't fill at the price that triggered them.
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '14px 0' }} />
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>Correlation Analysis</div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>
            Compare your asset's returns against other tickers or macro data. Results appear alongside backtest output.
          </div>

          <label style={labelStyle}>Compare Tickers</label>
          <input
            value={corrTickers}
            onChange={(e) => setCorrTickers(e.target.value)}
            placeholder="e.g. SPY, QQQ, GLD"
            style={inputStyle}
          />
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
            Comma-separated ticker symbols (up to 5)
          </div>

          <label style={{ ...labelStyle, marginTop: 10 }}>Compare External Data</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
            {[
              { value: 'vix', label: 'VIX' },
              { value: 'gold', label: 'Gold' },
              { value: 'us_dollar', label: 'USD Index' },
              { value: 'treasury_10y', label: '10Y Treasury' },
              { value: 'treasury_2y', label: '2Y Treasury' },
              { value: 'treasury_spread', label: '10Y-2Y Spread' },
              { value: 'fed_funds_rate', label: 'Fed Funds' },
              { value: 'unemployment', label: 'Unemployment' },
              { value: 'consumer_sentiment', label: 'Sentiment' },
            ].map((opt) => {
              const selected = corrExternal.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    setCorrExternal((prev) =>
                      selected ? prev.filter((v) => v !== opt.value) : [...prev, opt.value].slice(0, 5)
                    );
                  }}
                  style={{
                    padding: '4px 10px',
                    fontSize: 11,
                    fontWeight: selected ? 700 : 500,
                    border: selected ? '1.5px solid #0ea5e9' : '1px solid var(--border)',
                    borderRadius: 14,
                    background: selected ? 'rgba(14,165,233,0.15)' : 'var(--surface)',
                    color: selected ? '#0ea5e9' : 'var(--muted)',
                    cursor: 'pointer',
                  }}
                >
                  {selected ? '✓ ' : ''}{opt.label}
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
            Select up to 5 external data sources
          </div>
        </div>
      )}

      {queued && (
        <div style={{
          marginTop: 16,
          padding: '14px 16px',
          background: 'rgba(20, 184, 166, 0.1)',
          border: '1px solid var(--teal)',
          borderRadius: 8,
          fontSize: 13,
          color: 'var(--teal)',
          lineHeight: 1.5,
        }}>
          <strong>Running in the background</strong> — this is a large backtest so it's been queued.
          You'll receive a notification when it's done.
        </div>
      )}

      {error && (
        <div style={{ fontSize: 13, marginTop: 8, color: 'var(--red)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ flex: 1 }}>{error}</span>
          <button
            onClick={() => navigate('/pricing')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--red)', fontWeight: 700, textDecoration: 'underline', padding: 0, whiteSpace: 'nowrap' }}
          >View plans →</button>
        </div>
      )}

      <button
        onClick={handleRun}
        disabled={running || queued}
        style={{
          marginTop: 16,
          width: '100%',
          padding: '12px 0',
          fontSize: 15,
          fontWeight: 700,
          background: (running || queued) ? 'var(--subtle)' : 'var(--teal)',
          color: 'var(--bg)',
          border: 'none',
          borderRadius: 8,
          cursor: (running || queued) ? 'not-allowed' : 'pointer',
        }}
      >
        {running ? 'Running backtest…' : queued ? 'Queued' : 'Run Backtest'}
      </button>
    </div>
  );
}

const labelStyle = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text)',
  marginTop: 14,
  marginBottom: 4,
};

const fieldMsgStyle = {
  fontSize: 11,
  color: 'var(--red, #f85149)',
  marginTop: 4,
};

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  fontSize: 14,
  border: '1px solid var(--border)',
  borderRadius: 6,
  boxSizing: 'border-box',
  background: 'var(--surface)',
  color: 'var(--text)',
};

const invalidInputStyle = {
  border: '1px solid var(--red)',
};
